import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { Badge } from '@/components/ui/badge';
import { useProfileById } from '@/hooks/useProfile';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Building2, MapPin, Heart, ExternalLink, ArrowLeft, MessageSquare, Briefcase, Globe, ShieldBan, Flag } from 'lucide-react';
import { useBlockUser, useUnblockUser, useBlocks } from '@/hooks/useBlocks';
import { ReportDialog } from '@/components/ReportDialog';
import { useToast } from '@/hooks/use-toast';
import { useMembershipContext } from '@/components/layout/AppLayout';

const BIO_MARKERS = [
  { prefix: 'Current Title:', label: 'Title', isList: false },
  { prefix: 'Current Company:', label: 'Company', isList: false },
  { prefix: 'Primary Function:', label: 'Function', isList: false },
  { prefix: 'Contributing to QBN:', label: 'Contributing', isList: true },
  { prefix: 'Interested in through QBN:', label: 'Interested in', isList: true },
];

const TAG_COLORS = [
  'bg-pink-500/15 border-pink-500/30 text-pink-600 dark:text-pink-300',
  'bg-teal-500/15 border-teal-500/30 text-teal-600 dark:text-teal-300',
  'bg-violet-500/15 border-violet-500/30 text-violet-600 dark:text-violet-300',
  'bg-amber-500/15 border-amber-500/30 text-amber-600 dark:text-amber-300',
  'bg-sky-500/15 border-sky-500/30 text-sky-600 dark:text-sky-300',
];

interface BioSection {
  label: string;
  value: string;
  isList: boolean;
}

function parseBioSections(bio: string | null): BioSection[] | null {
  if (!bio?.trim()) return null;

  const sections: BioSection[] = [];
  const paragraphs = bio.split('\n\n');

  for (const para of paragraphs) {
    const trimmed = para.trim();
    if (!trimmed) continue;

    const matched = BIO_MARKERS.find((m) => trimmed.startsWith(m.prefix));
    if (matched) {
      const value = trimmed.slice(matched.prefix.length).trim();
      if (value) sections.push({ label: matched.label, value, isList: matched.isList });
    }
  }

  return sections.length > 0 ? sections : null;
}

export default function MemberProfilePage() {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: profile, isLoading } = useProfileById(userId);

  const { data: blocks } = useBlocks();
  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();
  const { toast } = useToast();
  const [reportOpen, setReportOpen] = useState(false);

  const { isPending: isCurrentUserPending } = useMembershipContext();
  const isOwnProfile = user?.id === userId;
  const isBlocked = blocks?.some((b) => b.blocked_id === userId) ?? false;
  const bioSections = parseBioSections(profile?.bio ?? null);
  const hasRawBio = !!profile?.bio?.trim() && !bioSections;

  const handleToggleBlock = async () => {
    if (!userId) return;
    try {
      if (isBlocked) {
        await unblockUser.mutateAsync(userId);
        toast({ title: 'User unblocked' });
      } else {
        const confirmed = window.confirm(
          'Block this member? You will no longer see their posts or profile.'
        );
        if (!confirmed) return;
        await blockUser.mutateAsync(userId);
        toast({ title: 'User blocked', description: 'Their content will be hidden from your feed and directory.' });
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Member not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Back button */}
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>

      {/* Profile Card */}
      <Card className="card-qbn overflow-hidden">
        {/* Banner */}
        <div className="h-24 bg-gradient-primary" />

        <CardContent className="relative pt-0 pb-6">
          {/* Avatar */}
          <div className="relative w-24 -mt-12 mb-4">
            <Avatar className="h-24 w-24 border-4 border-card">
              <SignedAvatarImage src={profile.avatar_url || undefined} />
              <AvatarFallback className="bg-gradient-primary text-white text-2xl font-display">
                {profile.full_name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-foreground">{profile.full_name}</h2>
                {profile.is_mentor && (
                  <Badge className="badge-mentor">
                    <Heart className="h-3 w-3 mr-1" />
                    Matchups
                  </Badge>
                )}
              </div>
              {profile.pronouns && (
                <span className="text-sm text-muted-foreground">({profile.pronouns})</span>
              )}
            </div>

            {profile.headline && (
              <p className="text-muted-foreground">{profile.headline}</p>
            )}

            <div className="flex flex-wrap gap-4 text-sm">
              {profile.company && (
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  {profile.company}
                </span>
              )}
              {profile.city_cohort && (
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin className="h-4 w-4" />
                  {profile.city_cohort}
                </span>
              )}
              {profile.role && (
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Briefcase className="h-4 w-4" />
                  {profile.role}
                </span>
              )}
              {profile.industry && (
                <span className="flex items-center gap-1.5 text-muted-foreground">
                  <Globe className="h-4 w-4" />
                  {profile.industry}
                </span>
              )}
            </div>

            {/* Structured bio sections with tags */}
            {bioSections && (
              <div className="space-y-3 pt-2 border-t border-border">
                {bioSections.map((section) => (
                  <div key={section.label} className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {section.label}
                    </p>
                    {section.isList ? (
                      <div className="flex flex-wrap gap-1.5">
                        {section.value.split(',').map((item, i) => {
                          const trimmed = item.trim();
                          if (!trimmed) return null;
                          return (
                            <span
                              key={trimmed}
                              className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium border ${TAG_COLORS[i % TAG_COLORS.length]}`}
                            >
                              {trimmed}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-foreground">{section.value}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Raw bio fallback */}
            {hasRawBio && (
              <div className="text-foreground whitespace-pre-wrap break-words">
                {profile.bio}
              </div>
            )}

            <div className="flex gap-2 pt-2">
              {profile.linkedin_url && !isCurrentUserPending && (
                <Button variant="outline" size="sm" asChild>
                  <a href={profile.linkedin_url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    LinkedIn
                  </a>
                </Button>
              )}
              {!isOwnProfile && !isCurrentUserPending && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/messages?to=${userId}`)}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Message
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleBlock}
                    disabled={blockUser.isPending || unblockUser.isPending}
                  >
                    <ShieldBan className="h-4 w-4 mr-2" />
                    {isBlocked ? 'Unblock' : 'Block'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setReportOpen(true)}
                  >
                    <Flag className="h-4 w-4 mr-2" />
                    Report
                  </Button>
                </>
              )}
              {isOwnProfile && (
                <Button
                  size="sm"
                  className="btn-qbn-primary"
                  onClick={() => navigate('/profile')}
                >
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <ReportDialog
        open={reportOpen}
        onOpenChange={setReportOpen}
        reportedUserId={userId}
        reportedName={profile?.full_name}
      />
    </div>
  );
}
